import { lazy } from "react";
import { redirect, type RouteObject } from "react-router";
import { featureFlags } from "./config/featureFlags";

/**
 * The public route subtree, shared by the browser router (routes.ts) and the
 * build-time prerender (src/entry-server.tsx). Keep this module free of
 * browser-only side effects: it is evaluated in Node during `npm run build`.
 */

export const PublicLayout = lazy(() =>
  import("@/components/layouts/PublicLayout").then((m) => ({ default: m.PublicLayout }))
);
export const NotFoundPage = lazy(() => import("@/components/pages/NotFoundPage"));

// Public pages — lazy loaded for route-level code-splitting, not to avoid a
// "Vite optimizer deadlock" (that diagnosis was wrong; see
// docs/WEB_CLIENT_BLANK_SCREEN_FIX_PLAN.md for the real incident history).
const HomePage = lazy(() => import("@/components/pages/HomePage"));
const AboutPage = lazy(() => import("@/components/pages/AboutPage"));
const ContactPage = lazy(() => import("@/components/pages/ContactPage"));
const FaqsPage = lazy(() => import("@/components/pages/FaqsPage"));
const InviteEarnPage = lazy(() => import("@/components/pages/InviteEarnPage"));
const SupportPage = lazy(() => import("@/components/pages/SupportPage"));
const PrivacyPolicyPage = lazy(() => import("@/components/pages/PrivacyPolicyPage"));
const SecurityPolicyPage = lazy(() => import("@/components/pages/SecurityPolicyPage"));
const TermsPage = lazy(() => import("@/components/pages/TermsPage"));
const InternationalMoneyTransferPage = lazy(
  () => import("@/components/pages/InternationalMoneyTransferPage")
);
const ExchangeRatesPage = lazy(() => import("@/components/pages/ExchangeRatesPage"));
const RequestMoneyPage = lazy(() => import("@/components/pages/RequestMoneyPage"));
const BlogPage = lazy(() => import("@/components/pages/BlogPage"));
const PromotionsPage = lazy(() => import("@/components/pages/PromotionsPage"));

/**
 * The 14 public pages of the redesign (SPEC 2), plus redirects for removed
 * routes. Blog and Promotions render the 404 page until their feature flags
 * are switched on (open items 7 and 13).
 */
export const publicRoutes: RouteObject[] = [
  { index: true, Component: HomePage },
  { path: "international-money-transfer", Component: InternationalMoneyTransferPage },
  { path: "exchange-rates", Component: ExchangeRatesPage },
  { path: "request-money", Component: RequestMoneyPage },
  { path: "about-us", Component: AboutPage },
  { path: "blog", Component: featureFlags.enableBlog ? BlogPage : NotFoundPage },
  { path: "faqs", Component: FaqsPage },
  { path: "contact-us", Component: ContactPage },
  { path: "support", Component: SupportPage },
  { path: "invite-and-earn", Component: InviteEarnPage },
  { path: "promotions", Component: featureFlags.enablePromotions ? PromotionsPage : NotFoundPage },
  { path: "terms-and-conditions", Component: TermsPage },
  { path: "privacy-policy", Component: PrivacyPolicyPage },
  { path: "security-policy", Component: SecurityPolicyPage },

  // Removed from the new sitemap: keep old links working.
  { path: "download", loader: () => redirect("/#app") },
  { path: "global-accounts", loader: () => redirect("/exchange-rates") },
];

export const publicRouteTree: RouteObject = {
  path: "/",
  Component: PublicLayout,
  children: publicRoutes,
};
